import { useCallback, useEffect, useRef, useState } from "react";

import { useIsMounted } from "@smart/itops-hooks-dom";
import {
  buildGetRelatedContacts,
  useSmokeballApp,
} from "@smart/itops-smokeball-app-dom";
import { Contact, Role } from "@smart/itops-smokeball-app-dom/src/types";
import { buildDisplayName } from "@smart/itops-utils-basic";
import { useLazyLoadMatterRoles } from "@smart/manage-gql-client-dom";

export type RoleContact = Contact & { email?: string | null; role: Role };

export type RoleContacts = {
  client: RoleContact | undefined;
  roleContacts: RoleContact[] | undefined;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

export const buildRoleContactName = (
  roleContact: RoleContact,
  option?: { hideRole?: boolean },
) => {
  let namePart;
  if (roleContact.person && Object.keys(roleContact.person).length > 0) {
    namePart = buildDisplayName(roleContact.person, roleContact.id);
  } else if (roleContact.company) {
    namePart = roleContact.company.name || roleContact.id;
  } else if (roleContact.trust) {
    namePart = roleContact.trust.name || roleContact.id;
  }

  return [namePart, !option?.hideRole && roleContact.role.name]
    .filter(Boolean)
    .join(" - ");
};

export const useRoleContacts = ({
  matterId,
}: {
  matterId?: string | null;
} = {}): RoleContacts => {
  const [isLoading, setIsLoading] = useState(false);
  const [roleContacts, setRoleContacts] = useState<RoleContact[]>();
  const [client, setClient] = useState<RoleContact>();
  const getRoles = useLazyLoadMatterRoles();
  const { isMounted } = useIsMounted();
  const latestRequestRef = useRef(0);

  const { roles, contacts, hostType, context } = useSmokeballApp();

  const fetchRoleContacts = useCallback(async () => {
    /**
     * Use the API instead for the "web" host as the SDK is not fully functional.
     * Refer to https://smokeball.atlassian.net/browse/LINK-1662 for more details.
     */
    if (
      !contacts ||
      (!roles && hostType !== "web") ||
      (!matterId && (!contacts?.current || !context?.matterId))
    )
      return;

    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    setIsLoading(true);

    const existingRoles =
      hostType === "web" || matterId
        ? await getRoles({ matterId: matterId || context?.matterId || "" })
        : (await roles!.get()).roles;
    const getMatterContacts = async () => {
      if (matterId) {
        const matterRoleContacts = await Promise.all(
          (existingRoles || [])
            .filter((role) => role.contactId)
            .map((role) => contacts.get(role.contactId!)),
        );
        return buildGetRelatedContacts(contacts.get)(matterRoleContacts);
      }
      return contacts?.current || [];
    };
    const matterContacts = await getMatterContacts();
    const existingRoleContacts = (existingRoles || [])
      .flatMap((role) => {
        const contact = matterContacts.find((c) => c.id === role.contactId);
        if (!contact) return [];
        /**
         * groupOfPeople.people in smokeball version 8.6 is { id: string }[]
         * and it changes to string[] in version 8.8
         * The check should be removed once all clients have upgraded to 8.8
         */
        const groupContactIds =
          (contact.groupOfPeople?.people?.map((p: any) =>
            typeof p === "string" ? p : p.id,
          ) as string[] | undefined) || [];
        const contactIds = [
          contact.id,
          ...(role.representativeIds || []).filter(Boolean),
          ...groupContactIds,
        ];

        const roleContactCandidates = matterContacts
          .filter((c) => contactIds.some((id) => id === c.id))
          .map((c) => ({
            ...c,
            email: c.person?.email || c.company?.email,
            role,
          }));

        /**
         * Prefer person representatives over the company/trust contact when
         * a role resolves to both (e.g. a company role with a linked
         * individual representative). This must stay scoped to this
         * role's own candidates - filtering across all roles would let a
         * person contact on one role wipe out an unrelated role's valid
         * company/trust contact.
         */
        return roleContactCandidates.some((c) => !!c.person)
          ? roleContactCandidates.filter((c) => !!c.person)
          : roleContactCandidates;
      })
      .sort((a, b) => a.lastUpdated - b.lastUpdated) as RoleContact[];

    if (isMounted() && latestRequestRef.current === requestId) {
      setRoleContacts(existingRoleContacts);
      setClient(
        existingRoleContacts.find(
          (c) => c.role.isClient && (c.person || c.company || c.trust),
        ),
      );
      setIsLoading(false);
    }
  }, [contacts?.current, matterId, roles, hostType, context?.matterId]);

  useEffect(() => {
    fetchRoleContacts().catch((err) => console.error(err));
  }, [fetchRoleContacts]);

  return { client, roleContacts, isLoading, refetch: fetchRoleContacts };
};
